import { useNavigate, useLocation } from "react-router-dom";

export interface ModalNavigationOptions {
  /**
   * Additional state to pass to the modal
   */
  state?: ModalState;
  /**
   * Replace current history entry instead of pushing new one
   */
  replace?: boolean;
}

export interface ModalState {
  /**
   * The background location to return to when modal closes
   */
  background?: Location;
  /**
   * Any additional state data
   */
  [key: string]: any;
}

export const useModalNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Navigate to a modal route with background location state
   */
  const openModal = (
    modalPath: string,
    options: ModalNavigationOptions = {},
  ) => {
    const currentState = location.state as ModalState;
    const { state = {}, replace = false } = options;

    navigate(modalPath, {
      state: {
        background: location,
        ...currentState,
        ...state,
      },
      replace,
    });
  };

  /**
   * Close the modal and return to background location
   */
  const closeModal = () => {
    navigate(-1);
  };

  /**
   * Get the current modal state from location
   */
  const getModalState = (): ModalState | null => {
    const state = location.state as ModalState;
    return state?.background ? state : null;
  };

  /**
   * Check if currently in a modal context
   */
  const isModal = (): boolean => {
    return getModalState() !== null;
  };

  /**
   * Get the background location if in modal context
   */
  const getBackgroundLocation = (): Location | null => {
    const modalState = getModalState();
    return modalState?.background || null;
  };

  return {
    openModal,
    closeModal,
    getModalState,
    isModal,
    getBackgroundLocation,
  };
};
